import { useState } from 'react';
import { type Language, t } from '../i18n';
import type { ProjectFormData } from '../hooks/useChat';

interface ProjectFormProps {
  language: Language;
  onSubmit: (data: ProjectFormData, contactEmail?: string) => void;
}

const PROJECT_TYPES = ['web-app', 'mobile-app', 'internal-tool', 'api-integration', 'e-commerce', 'ai-ml', 'other'] as const;
const TARGET_USERS = ['internal', 'external', 'both', 'b2b'] as const;
const COMPANY_SIZES = ['solo', '2-10', '11-50', '51-200', '200+'] as const;
const TECHNICAL_TEAMS = ['none', 'some', 'full', 'looking'] as const;
const PRIMARY_GEOGRAPHIES = ['Italy', 'Europe', 'Global'];
const OTHER_GEOGRAPHIES = ['US', 'LATAM', 'MENA', 'East Europe', 'Asia'];

const INDUSTRIES = [
  'Technology/SaaS',
  'Finance/Banking',
  'Retail/E-commerce',
  'Healthcare',
  'Education',
  'Tourism/Hospitality',
  'Food/Agriculture',
  'Fashion/Textile',
  'Manufacturing',
  'Construction',
  'Energy/Utilities',
  'Real Estate',
  'Professional Services',
  'Government/Public Sector',
  'Other',
];

const BUDGETS_IT = [
  { value: '< 10.000 EUR', label: 'Meno di 10.000 EUR' },
  { value: '10.000 - 30.000 EUR', label: '10.000 - 30.000 EUR' },
  { value: '30.000 - 75.000 EUR', label: '30.000 - 75.000 EUR' },
  { value: '75.000 - 150.000 EUR', label: '75.000 - 150.000 EUR' },
  { value: '150.000 - 300.000 EUR', label: '150.000 - 300.000 EUR' },
  { value: '> 300.000 EUR', label: 'Oltre 300.000 EUR' },
  { value: 'Da definire', label: 'Da definire' },
];

const BUDGETS_EN = [
  { value: '< 10,000 EUR', label: 'Under 10,000 EUR' },
  { value: '10,000 - 30,000 EUR', label: '10,000 - 30,000 EUR' },
  { value: '30,000 - 75,000 EUR', label: '30,000 - 75,000 EUR' },
  { value: '75,000 - 150,000 EUR', label: '75,000 - 150,000 EUR' },
  { value: '150,000 - 300,000 EUR', label: '150,000 - 300,000 EUR' },
  { value: '> 300,000 EUR', label: 'Over 300,000 EUR' },
  { value: 'TBD', label: 'To be defined' },
];

const TIMELINES_IT = [
  { value: '1-3 mesi', label: '1-3 mesi' },
  { value: '3-6 mesi', label: '3-6 mesi' },
  { value: '6-12 mesi', label: '6-12 mesi' },
  { value: '12+ mesi', label: 'Oltre 12 mesi' },
  { value: 'Da definire', label: 'Da definire' },
];

const TIMELINES_EN = [
  { value: '1-3 months', label: '1-3 months' },
  { value: '3-6 months', label: '3-6 months' },
  { value: '6-12 months', label: '6-12 months' },
  { value: '12+ months', label: 'Over 12 months' },
  { value: 'TBD', label: 'To be defined' },
];

export function ProjectForm({ language, onSubmit }: ProjectFormProps) {
  const [formData, setFormData] = useState<ProjectFormData>({
    projectDescription: '',
    projectType: 'web-app',
    targetUsers: 'external',
    industry: '',
    companySize: '2-10',
    budget: '',
    timeline: '',
    technicalTeam: 'none',
    existingSystems: '',
    hasDesign: false,
    geographicFocus: [],
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ProjectFormData, boolean>>>({});
  const [showOtherGeo, setShowOtherGeo] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [emailError, setEmailError] = useState(false);

  const budgets = language === 'it' ? BUDGETS_IT : BUDGETS_EN;
  const timelines = language === 'it' ? TIMELINES_IT : TIMELINES_EN;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Partial<Record<keyof ProjectFormData, boolean>> = {};
    if (!formData.projectDescription.trim()) newErrors.projectDescription = true;
    if (!formData.industry) newErrors.industry = true;
    if (!formData.budget) newErrors.budget = true;
    if (!formData.timeline) newErrors.timeline = true;
    if (formData.geographicFocus.length === 0) newErrors.geographicFocus = true;

    const trimmedEmail = contactEmail.trim();
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError(true);
      return;
    }
    setEmailError(false);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData, trimmedEmail || undefined);
  };

  const handleGeoToggle = (geo: string) => {
    setFormData((prev) => ({
      ...prev,
      geographicFocus: prev.geographicFocus.includes(geo)
        ? prev.geographicFocus.filter((g) => g !== geo)
        : [...prev.geographicFocus, geo],
    }));
    setErrors((prev) => ({ ...prev, geographicFocus: false }));
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Section */}
      <div className="relative mb-8 rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-primary-50 dark:bg-primary-50/10" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-100/60 rounded-full blur-3xl dark:bg-primary-100/10" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-100/40 rounded-full blur-2xl dark:bg-primary-100/5" />

        <div className="relative z-10 px-8 py-10 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            {t(language, 'project.hero.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            {t(language, 'project.hero.subtitle')}
          </p>

          <div className="flex justify-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-primary">
                {t(language, 'project.hero.stat1')}
              </div>
              <div className="text-sm text-muted-foreground">
                {t(language, 'project.hero.stat1Label')}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-primary">
                {t(language, 'project.hero.stat2')}
              </div>
              <div className="text-sm text-muted-foreground">
                {t(language, 'project.hero.stat2Label')}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-primary">
                {t(language, 'project.hero.stat3')}
              </div>
              <div className="text-sm text-muted-foreground">
                {t(language, 'project.hero.stat3Label')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card p-6">
        <h2 className="font-semibold text-foreground mb-6 text-lg">
          {t(language, 'project.form.title')}
        </h2>

        {/* Project Description */}
        <div className="mb-6">
          <label htmlFor="projectDescription" className="block text-xs font-medium text-foreground mb-1">
            {t(language, 'project.form.projectDescription.label')} *
          </label>
          <textarea
            id="projectDescription"
            value={formData.projectDescription}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, projectDescription: e.target.value }));
              setErrors((prev) => ({ ...prev, projectDescription: false }));
            }}
            rows={3}
            maxLength={3000}
            className={`w-full px-3 py-2 text-sm border rounded-lg bg-card text-foreground focus:border-primary focus:ring-1 focus:ring-primary resize-none ${
              errors.projectDescription ? 'border-red-500' : 'border-border'
            }`}
            placeholder={t(language, 'project.form.projectDescription.placeholder')}
          />
          <div className="text-xs text-muted-foreground mt-1 text-right">
            {formData.projectDescription.length}/3000
          </div>
        </div>

        {/* Row: Project Type, Target Users, Industry */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label htmlFor="projectType" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'project.form.projectType.label')}
            </label>
            <select
              id="projectType"
              value={formData.projectType}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                projectType: e.target.value as ProjectFormData['projectType'],
              }))}
              className="form-select text-sm py-2"
            >
              {PROJECT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(language, `project.form.projectType.options.${type}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="targetUsers" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'project.form.targetUsers.label')}
            </label>
            <select
              id="targetUsers"
              value={formData.targetUsers}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                targetUsers: e.target.value as ProjectFormData['targetUsers'],
              }))}
              className="form-select text-sm py-2"
            >
              {TARGET_USERS.map((tu) => (
                <option key={tu} value={tu}>
                  {t(language, `project.form.targetUsers.options.${tu}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="industry" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'project.form.industry.label')} *
            </label>
            <select
              id="industry"
              value={formData.industry}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, industry: e.target.value }));
                setErrors((prev) => ({ ...prev, industry: false }));
              }}
              className={`form-select text-sm py-2 ${errors.industry ? 'border-red-500' : ''}`}
            >
              <option value="">{t(language, 'project.form.industry.placeholder')}</option>
              {INDUSTRIES.map((ind) => (
                <option key={ind} value={ind}>
                  {t(language, `form.industry.options.${ind}`) !== `form.industry.options.${ind}`
                    ? t(language, `form.industry.options.${ind}`)
                    : ind}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row: Company Size, Technical Team */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label htmlFor="companySize" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'project.form.companySize.label')}
            </label>
            <select
              id="companySize"
              value={formData.companySize}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                companySize: e.target.value as ProjectFormData['companySize'],
              }))}
              className="form-select text-sm py-2"
            >
              {COMPANY_SIZES.map((size) => (
                <option key={size} value={size}>
                  {t(language, `project.form.companySize.options.${size}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="technicalTeam" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'project.form.technicalTeam.label')}
            </label>
            <select
              id="technicalTeam"
              value={formData.technicalTeam}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                technicalTeam: e.target.value as ProjectFormData['technicalTeam'],
              }))}
              className="form-select text-sm py-2"
            >
              {TECHNICAL_TEAMS.map((team) => (
                <option key={team} value={team}>
                  {t(language, `project.form.technicalTeam.options.${team}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row: Budget, Timeline */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <div>
            <label htmlFor="budget" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'project.form.budget.label')} *
            </label>
            <select
              id="budget"
              value={formData.budget}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, budget: e.target.value }));
                setErrors((prev) => ({ ...prev, budget: false }));
              }}
              className={`form-select text-sm py-2 ${errors.budget ? 'border-red-500' : ''}`}
            >
              <option value="">{t(language, 'project.form.budget.placeholder')}</option>
              {budgets.map((b) => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="timeline" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'project.form.timeline.label')} *
            </label>
            <select
              id="timeline"
              value={formData.timeline}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, timeline: e.target.value }));
                setErrors((prev) => ({ ...prev, timeline: false }));
              }}
              className={`form-select text-sm py-2 ${errors.timeline ? 'border-red-500' : ''}`}
            >
              <option value="">{t(language, 'project.form.timeline.placeholder')}</option>
              {timelines.map((tl) => (
                <option key={tl.value} value={tl.value}>{tl.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Existing Systems */}
        <div className="mb-6">
          <label htmlFor="existingSystems" className="block text-xs font-medium text-foreground mb-1">
            {t(language, 'project.form.existingSystems.label')}
          </label>
          <input
            type="text"
            id="existingSystems"
            value={formData.existingSystems}
            onChange={(e) => setFormData((prev) => ({ ...prev, existingSystems: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:border-primary focus:ring-1 focus:ring-primary"
            placeholder={t(language, 'project.form.existingSystems.placeholder')}
          />
        </div>

        {/* Geographic Focus */}
        <fieldset className="mb-4">
          <legend className="block text-xs font-medium text-foreground mb-2">
            {t(language, 'project.form.geographicFocus.label')} *
          </legend>
          <div className="flex flex-wrap gap-2">
            {PRIMARY_GEOGRAPHIES.map((geo) => (
              <label
                key={geo}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer transition-colors text-sm ${
                  formData.geographicFocus.includes(geo)
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-card border-border text-foreground hover:border-primary/50'
                } ${errors.geographicFocus ? 'border-destructive' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={formData.geographicFocus.includes(geo)}
                  onChange={() => handleGeoToggle(geo)}
                  className="w-3.5 h-3.5 accent-primary rounded"
                />
                <span>{t(language, `form.geography.options.${geo}`)}</span>
              </label>
            ))}
            <label
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer transition-colors text-sm ${
                showOtherGeo
                  ? 'bg-muted border-muted-foreground/50 text-foreground'
                  : 'bg-card border-border text-foreground hover:border-primary/50'
              }`}
            >
              <input
                type="checkbox"
                checked={showOtherGeo}
                onChange={() => setShowOtherGeo(!showOtherGeo)}
                className="w-3.5 h-3.5 accent-primary rounded"
              />
              <span>{t(language, 'form.geography.other')}</span>
            </label>
          </div>
          {showOtherGeo && (
            <div className="mt-3 pl-4 border-l-2 border-border">
              <div className="flex flex-wrap gap-2">
                {OTHER_GEOGRAPHIES.map((geo) => (
                  <label
                    key={geo}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer transition-colors text-sm ${
                      formData.geographicFocus.includes(geo)
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-card border-border text-foreground hover:border-primary/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.geographicFocus.includes(geo)}
                      onChange={() => handleGeoToggle(geo)}
                      className="w-3.5 h-3.5 accent-primary rounded"
                    />
                    <span>{t(language, `form.geography.options.${geo}`)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </fieldset>

        {/* Has Design */}
        <div className="mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.hasDesign}
              onChange={(e) => setFormData((prev) => ({ ...prev, hasDesign: e.target.checked }))}
              className="w-4 h-4 accent-primary rounded"
            />
            <span className="text-sm text-foreground">
              {t(language, 'project.form.hasDesign.label')}
            </span>
          </label>
        </div>

        {/* Optional contact email (captures partial leads) */}
        <fieldset>
          <label className="text-sm font-medium mb-1 block">
            {language === 'it' ? 'Email (opzionale)' : 'Email (optional)'}
          </label>
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => {
              setContactEmail(e.target.value);
              setEmailError(false);
            }}
            placeholder={language === 'it' ? 'tu@azienda.it' : 'you@company.com'}
            className={`form-input w-full ${emailError ? 'border-red-500' : ''}`}
          />
          <p className="text-xs text-muted mt-1">
            {language === 'it'
              ? 'Ti invieremo il report via email quando sarà pronto. Puoi anche lasciarla dopo.'
              : 'We\'ll send the report to this address when ready. You can also add it later.'}
          </p>
          {emailError && (
            <p className="text-xs text-red-500 mt-1">
              {language === 'it' ? 'Formato email non valido' : 'Invalid email format'}
            </p>
          )}
        </fieldset>

        {/* Submit */}
        <button type="submit" className="btn-primary w-full py-3 text-base font-medium">
          {t(language, 'project.form.submit')}
        </button>
      </form>
    </div>
  );
}
